import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Button, Space, Divider } from "antd";
import {
    BoldOutlined,
    ItalicOutlined,
    UnorderedListOutlined,
    OrderedListOutlined,
    LinkOutlined,
} from "@ant-design/icons";
import css from "./ClientBuilderPanel.module.css";

// Only the marks/nodes the real Terms/Privacy content actually uses today
// (h2/h3, p, ul/li, ol/li, strong, em, a) — confirmed against both pages'
// real content before building this, not a speculative full toolbar.
// StarterKit ships h1-h6 by default; restricted to levels 2-3 since page
// titles (h1) are owned by the Default/Structural page shell, not admin
// content — matches the real pages' own structure (h1 title outside the
// body, h2 section headings inside it).
function RichTextEditorField({ value, onChange, dir }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Link.configure({ openOnClick: false, autolink: true }),
        ],
        content: value || "",
        onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    });

    // Tiptap is uncontrolled internally — when the admin switches the
    // language dropdown, `value` changes out from under the editor without
    // it knowing, so it must be re-synced explicitly. Skipped when the
    // incoming value already matches the editor's own current HTML, so
    // typing doesn't fight this effect on every keystroke.
    useEffect(() => {
        if (!editor) return;
        const next = value || "";
        if (editor.getHTML() !== next) editor.commands.setContent(next, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    useEffect(() => {
        if (!editor) return;
        editor.view.dom.setAttribute("dir", dir || "ltr");
    }, [editor, dir]);

    if (!editor) return null;

    const setLink = () => {
        const previousUrl = editor.getAttributes("link").href;
        // eslint-disable-next-line no-alert
        const url = window.prompt("Link URL", previousUrl || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    return (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6 }}>
            <div style={{ display: "flex", alignItems: "center", padding: "4px 8px", borderBottom: "1px solid #e5e7eb" }}>
                <Space size={2}>
                    <Button
                        type={editor.isActive("bold") ? "primary" : "text"}
                        size="small"
                        icon={<BoldOutlined />}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    />
                    <Button
                        type={editor.isActive("italic") ? "primary" : "text"}
                        size="small"
                        icon={<ItalicOutlined />}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    />
                    <Divider type="vertical" style={{ margin: "0 2px" }} />
                    <Button
                        type={editor.isActive("heading", { level: 2 }) ? "primary" : "text"}
                        size="small"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    >
                        H2
                    </Button>
                    <Button
                        type={editor.isActive("heading", { level: 3 }) ? "primary" : "text"}
                        size="small"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    >
                        H3
                    </Button>
                    <Divider type="vertical" style={{ margin: "0 2px" }} />
                    <Button
                        type={editor.isActive("bulletList") ? "primary" : "text"}
                        size="small"
                        icon={<UnorderedListOutlined />}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    />
                    <Button
                        type={editor.isActive("orderedList") ? "primary" : "text"}
                        size="small"
                        icon={<OrderedListOutlined />}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    />
                    <Divider type="vertical" style={{ margin: "0 2px" }} />
                    <Button
                        type={editor.isActive("link") ? "primary" : "text"}
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={setLink}
                    />
                </Space>
            </div>
            <EditorContent
                editor={editor}
                className={css.richTextEditorContent}
            />
        </div>
    );
}

export { RichTextEditorField };
